import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Sun, Zap, ArrowRight, CheckCircle, TrendingDown, TrendingUp, Phone, ChevronRight, AlertCircle, UserRound } from 'lucide-react';
import axios from 'axios';
import logoImg from '../assets/logo-for-dark-bg-w300.png';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';

import { API } from '../lib/api';

// Energy range options (will be loaded from API)
const STEPS = {
  CONSUMPTION: 1,
  WHATSAPP: 2,
  RESULT: 3,
};

// Format currency BRL
const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

// Format WhatsApp input mask
const maskWhatsApp = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const PartnerLanding = () => {
  const { slug } = useParams();
  const [step, setStep] = useState(STEPS.CONSUMPTION);
  const [partnerData, setPartnerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [clientName, setClientName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [helperName, setHelperName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [scanRegistered, setScanRegistered] = useState(false);
  const [successCases, setSuccessCases] = useState([]);

  // Fetch partner data and register scan
  const fetchPartner = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/${slug}`);
      setPartnerData(res.data);
      setSuccessCases(res.data.success_cases || []);
    } catch (err) {
      setError('Parceiro não encontrado ou link inválido.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const registerScan = useCallback(async () => {
    if (scanRegistered) return;
    try {
      // Fetch real public IP so geolocation works even behind a proxy/localhost
      let ip = '';
      try {
        const ipRes = await axios.get('https://api.ipify.org?format=json', { timeout: 4000 });
        ip = ipRes.data?.ip || '';
      } catch {
        // silent — backend will try to extract from headers
      }

      await axios.post(`${API}/partner/${slug}/scan`, {
        user_agent: navigator.userAgent,
        ip
      });
      setScanRegistered(true);
    } catch {
      // Silent fail on tracking
    }
  }, [slug, scanRegistered]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  useEffect(() => {
    if (partnerData) registerScan();
  }, [partnerData, registerScan]);

  const handleSelectRange = (range) => {
    setSelectedRange(range);
    // Slight delay for animation
    setTimeout(() => setStep(STEPS.WHATSAPP), 350);
  };

  const handleWhatsAppSubmit = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) return;
    if (!whatsapp || whatsapp.replace(/\D/g, '').length < 10) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/${slug}/lead`, {
        name: clientName.trim(),
        whatsapp: whatsapp.replace(/\D/g, ''),
        helper_name: helperName.trim(),
        energy_range_label: selectedRange.label,
        investment_value: selectedRange.investment_value,
        monthly_savings: selectedRange.monthly_savings,
        monthly_return_pct: selectedRange.monthly_return_pct
      });
    } catch (err) {
      // Silent fail
    } finally {
      setSubmitting(false);
      setStep(STEPS.RESULT);
    }
  };

  const openWhatsApp = () => {
    if (!partnerData || !selectedRange) return;
    const pct = selectedRange.monthly_return_pct.toFixed(1).replace('.', ',');
    
    const v = selectedRange.investment_value;
    const invCash = formatCurrency(v);
    const downPayment = v * 0.10;
    const installment = ((v * 0.90) * 1.12) / 12;
    const investment = `${formatCurrency(downPayment)} de entrada + 12x de ${formatCurrency(installment)} ou ${invCash} à vista`;

    const partner = partnerData.partner_name;
    const waNumber = partnerData.agent_whatsapp;
    const greeting = clientName.trim() ? `Olá, sou *${clientName.trim()}*! ` : `Olá! `;

    const message = encodeURIComponent(
      `${greeting} Vim pelo QR Code do *${partner}*.\n\n` +
      `Gasto: *${selectedRange.label}*\n` +
      `Investimento: *${investment}*\n` +
      `Rentabilidade: *${pct}% ao mês*\n\n` +
      `Quero garantir minha visita técnica gratuita e a oferta com desconto!`
    );

    window.open(`https://wa.me/55${waNumber}?text=${message}`, '_blank');
  };

  // Live countdown — updates every second
  const offerValidity = useMemo(
    () => (partnerData?.table_validity ? new Date(partnerData.table_validity) : null),
    [partnerData?.table_validity]
  );

  const calcCountdown = (deadline) => {
    const diff = deadline - new Date();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    };
  };

  const [countdown, setCountdown] = useState(() =>
    offerValidity ? calcCountdown(offerValidity) : null
  );

  useEffect(() => {
    if (!offerValidity) return;
    const tick = () => {
      const c = calcCountdown(offerValidity);
      setCountdown(c);
      if (!c) {
        // Deadline crossed — reload to fetch updated prices from backend
        clearInterval(id);
        setTimeout(() => window.location.reload(), 500);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [offerValidity]);

  const offerDeadlineLabel = offerValidity
    ? offerValidity.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  // ─── Loading State ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  // ─── Error State ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Link inválido</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const progressPct = step === STEPS.CONSUMPTION ? 10 : step === STEPS.WHATSAPP ? 55 : 100;

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1613665813446-82a78c468a1d"
          alt="Solar Panels"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-950/95 to-orange-950/80" />
      </div>

      {/* Animated glow circles */}
      <div className="absolute top-10 right-10 w-72 h-72 bg-orange-600/15 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="px-4 py-5 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="h-8 flex items-center justify-center">
              <img src={logoImg} alt="3M Solar" className="w-full h-full object-contain" />
            </div>
          </div>

          {/* Partner badge */}
          {step === 1 && (
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 text-sm mt-5">
              <span className="text-orange-400">🤝</span>
              <span className="text-gray-200">
                Link exclusivo do parceiro{' '}
                <strong className="text-orange-300">{partnerData.partner_name}</strong>
              </span>
            </div>
          )}

        </header>

        {/* Progress bar */}
        <div className="px-4 mb-2 max-w-md mx-auto w-full">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5 px-1">
            <span className={step >= STEPS.CONSUMPTION ? 'text-orange-400 font-medium' : ''}>Consumo</span>
            <span className={step >= STEPS.WHATSAPP ? 'text-orange-400 font-medium' : ''}>Contato</span>
            <span className={step >= STEPS.RESULT ? 'text-orange-400 font-medium' : ''}>Resultado</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-start justify-center px-4 py-6">

          {/* ── STEP 1: Consumption ─────────────────────────────────────────── */}
          {step === STEPS.CONSUMPTION && (
            <div className="w-full max-w-md animate-fadeIn">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-orange-600/20 border border-orange-500/30 rounded-full px-3 py-1.5 text-xs text-orange-300 mb-4">
                  <Zap className="w-3.5 h-3.5" />
                  Simulação gratuita • 1 minuto
                </div>
                <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                  Quanto você gasta de{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                    energia por mês?
                  </span>
                </h1>
                <p className="text-gray-400 text-sm">
                  Selecione a faixa mais próxima da sua conta de luz
                </p>
              </div>

              <div className="space-y-3">
                {partnerData.ranges.map((range, idx) => (
                  <button
                    key={idx}
                    id={`range-btn-${idx}`}
                    onClick={() => handleSelectRange(range)}
                    className={`
                      w-full group relative flex items-center justify-between
                      bg-white/5 backdrop-blur-sm border rounded-2xl p-4
                      transition-all duration-200 hover:bg-orange-600/20 hover:border-orange-500/60
                      hover:shadow-lg hover:shadow-orange-600/20 hover:scale-[1.02]
                      ${selectedRange?.label === range.label
                        ? 'border-orange-500 bg-orange-600/25 scale-[1.02]'
                        : 'border-white/10'}
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-600/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-600/40 transition-colors">
                        <TrendingDown className="w-5 h-5 text-orange-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-white font-semibold text-sm">{range.label}</p>
                        <p className="text-gray-400 text-xs">Conta de energia</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* <div className="text-right hidden sm:block">
                        <p className="text-orange-300 font-bold text-sm">
                          Economize {formatCurrency(range.monthly_savings)}/mês
                        </p>
                        <p className="text-gray-500 text-xs">
                          com energia solar
                        </p>
                      </div> */}
                      <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-orange-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 2: WhatsApp ─────────────────────────────────────────────── */}
          {step === STEPS.WHATSAPP && (
            <div className="w-full max-w-md animate-fadeIn">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-600/30">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-3 leading-tight">
                  Ótima escolha! 🎉
                </h2>
                <p className="text-gray-300 text-base mb-1">
                  Só mais um passo para ver sua economia
                </p>
                <p className="text-gray-500 text-sm">
                  Informe seu nome e WhatsApp
                </p>
              </div>

              {/* Selected range summary */}
              {selectedRange && (
                <div className="bg-orange-600/15 border border-orange-500/30 rounded-2xl p-4 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <span className="text-gray-300">
                      Gasto selecionado:{' '}
                      <strong className="text-orange-300">{selectedRange.label}</strong>
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={handleWhatsAppSubmit} className="space-y-4">
                {/* Name field */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg select-none">
                    👤
                  </div>
                  <input
                    id="name-input"
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Seu nome"
                    required
                    autoComplete="name"
                    className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder:text-gray-500 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all"
                  />
                </div>

                {/* WhatsApp field */}
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <Phone className="w-5 h-5" />
                  </div>
                  <input
                    id="whatsapp-input"
                    type="tel"
                    inputMode="numeric"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(maskWhatsApp(e.target.value))}
                    placeholder="(38) 99999-9999"
                    required
                    className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder:text-gray-500 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all"
                  />
                </div>

                {/* Optional helper name field */}
                <div className="space-y-2">
                  <label htmlFor="helper-name-input" className="block text-sm font-semibold text-gray-200">
                    Quem te ajudou a acessar este formulário?
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <UserRound className="w-5 h-5" />
                    </div>
                    <input
                      id="helper-name-input"
                      type="text"
                      value={helperName}
                      onChange={(e) => setHelperName(e.target.value)}
                      placeholder="Nome da pessoa, se houver"
                      autoComplete="off"
                      aria-describedby="helper-name-help"
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder:text-gray-500 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm transition-all"
                    />
                  </div>
                  <p id="helper-name-help" className="text-gray-500 text-xs leading-relaxed px-1">
                    Opcional. Preencha apenas se alguém te ajudou com o QR Code ou com o formulário.
                  </p>
                </div>

                <p className="text-gray-500 text-xs text-center px-4">
                  🔒 Seus dados são privados e não serão compartilhados com terceiros.
                </p>

                <button
                  id="whatsapp-submit-btn"
                  type="submit"
                  disabled={submitting || !clientName.trim() || whatsapp.replace(/\D/g, '').length < 10}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl text-lg shadow-xl shadow-orange-600/30 flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] hover:shadow-orange-600/50 active:scale-[0.98]"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Calculando...
                    </>
                  ) : (
                    <>
                      Ver meu potencial de economia
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(STEPS.CONSUMPTION)}
                  className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors"
                >
                  ← Voltar
                </button>
              </form>
            </div>
          )}

          {/* ── STEP 3: Result ───────────────────────────────────────────────── */}
          {step === STEPS.RESULT && selectedRange && (
            <div className="w-full max-w-md animate-fadeIn">
              {/* Urgency countdown */}
              {countdown && (
                <div className="mb-6 text-center">
                  <p className="text-amber-400/90 font-semibold text-sm tracking-wide bg-amber-500/10 border border-amber-500/20 rounded-xl py-2.5 px-4 inline-block">
                    ⚡ Sua oferta expira em {countdown.days}d {countdown.hours}h {countdown.minutes}min {countdown.seconds}s
                  </p>
                </div>
              )}

              <h2 className="text-3xl font-bold text-white text-center mb-6 leading-tight">
                Seu resultado está{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                  aqui! ☀️
                </span>
              </h2>

              {/* Result Card */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/20 rounded-3xl p-6 mb-6 shadow-2xl">
                {/* Savings */}
                <div className="text-center mb-6 pb-6 border-b border-white/10">
                  <p className="text-gray-400 text-sm mb-1">Você pode economizar até</p>
                  <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                    {formatCurrency(selectedRange.monthly_savings)}
                  </p>
                  <p className="text-gray-400 text-sm mt-1">por mês na conta de energia ⚡</p>
                </div>

                {/* Investment & ROI */}
                <div className="space-y-4">
                  {/* Investment Row */}
                  <div className="flex items-start gap-3 pb-4 border-b border-white/10">
                    <div className="w-10 h-10 rounded-xl bg-orange-600/20 flex items-center justify-center shrink-0 mt-0.5 border border-orange-500/20">
                      <Zap className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-400 text-xs mb-1">Investimento promocional</p>
                      <p className="text-white font-extrabold text-lg sm:text-xl leading-snug whitespace-nowrap">
                        {formatCurrency(selectedRange.investment_value * 0.10)}
                        <span className="text-white font-normal text-xs sm:text-sm"> de entrada + </span>
                        <span className="text-white font-extrabold text-sm sm:text-base">12x</span>
                        <span className="text-white font-normal text-xs sm:text-sm"> de</span>{' '}
                        {formatCurrency(((selectedRange.investment_value * 0.90) * 1.12) / 12)}
                      </p>
                      <p className="text-amber-400/90 text-sm mt-1.5 font-medium">
                        ou <span className="whitespace-nowrap">{formatCurrency(selectedRange.investment_value)}</span> à vista
                      </p>
                    </div>
                  </div>

                  {/* Rentabilidade Row */}
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-green-600/25 flex items-center justify-center shrink-0 border border-green-500/20">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-gray-200 font-semibold text-sm">Rentabilidade estimada</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-black text-2xl leading-none">
                        {selectedRange.monthly_return_pct.toFixed(1).replace('.', ',')}%<span className="text-xs font-normal text-gray-400 ml-0.5">/mês</span>
                      </p>
                    </div>
                  </div>

                  {/* WhatsApp Proposal CTA */}
                  <button
                    onClick={openWhatsApp}
                    className="w-full font-bold py-3.5 rounded-2xl text-base flex items-center justify-center gap-2 transition-all duration-300 mt-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg shadow-green-600/25 hover:scale-[1.01] active:scale-[0.99]"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Receber Proposta no WhatsApp
                  </button>

                  {/* Investment comparison vs benchmarks */}
                  {(() => {
                    const pct = selectedRange.monthly_return_pct;
                    const POUPANCA = 0.50;   // ~0,50 % ao mês (média 2024)
                    const SELIC = 0.79;      // ~0,79 % ao mês (Tesouro Selic)
                    const items = [
                      { icon: '☀️', label: 'Energia Solar', value: pct.toFixed(1).replace('.', ','), color: 'text-orange-300', border: 'border-orange-500/50', bg: 'from-orange-600/25 to-amber-600/15', highlight: true },
                      { icon: '🏦', label: 'Poupança', value: POUPANCA.toFixed(2).replace('.', ','), color: 'text-gray-300', border: 'border-white/10', bg: null },
                      { icon: '📊', label: 'Tesouro Selic', value: SELIC.toFixed(2).replace('.', ','), color: 'text-gray-300', border: 'border-white/10', bg: null },
                    ];
                    return (
                      <div className="space-y-2 pt-2">
                        <p className="text-gray-400 text-xs text-center font-semibold uppercase tracking-widest mb-2">
                          Comparativo de rentabilidade / mês
                        </p>
                        {items.map(({ icon, label, value, color, border, bg, highlight }) => (
                          <div
                            key={label}
                            className={`flex items-center gap-3 px-4 py-1.5 rounded-xl border ${border} ${highlight ? `bg-gradient-to-r ${bg} border-2` : 'bg-white/5'}`}
                          >
                            <span className="text-base">{icon}</span>
                            <div className="flex-1">
                              <p className={`text-xs font-medium ${highlight ? 'text-orange-300' : 'text-gray-300'}`}>{label}</p>
                            </div>
                            <p className={`${highlight ? 'font-extrabold' : 'font-bold'} text-base leading-none ${color}`}>
                              {value}%<span className="text-[10px] font-normal text-gray-500 ml-0.5">/mês</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 justify-center mb-6">
                {[
                  '✅ Sem burocracia',
                  '✅ Instalação inclusa',
                  '✅ Garantia de 10 anos',
                  '✅ Sem obras grandes',
                ].map((item, i) => (
                  <span key={i} className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-gray-300">
                    {item}
                  </span>
                ))}
              </div>

              {successCases.length > 0 && (
                <div className="mb-6">
                  <div className="text-center mb-4">
                    <p className="text-orange-300 text-xs font-semibold uppercase tracking-[0.28em] mb-2">
                      Cases de sucesso
                    </p>
                    <h3 className="text-xl font-bold text-white">
                      Veja quem já está economizando
                    </h3>
                  </div>
                  <Carousel opts={{ align: 'start', loop: successCases.length > 1 }} className="w-full">
                    <CarouselContent>
                      {successCases.map((successCase) => (
                        <CarouselItem key={successCase.id} className="basis-[88%] sm:basis-[78%]">
                          <div className="overflow-hidden rounded-3xl border border-white/15 bg-white/5 backdrop-blur-sm shadow-2xl">
                            <img src={successCase.image} alt={successCase.title} className="h-48 w-full object-cover" />
                            <div className="p-5 text-left">
                              <p className="text-white font-bold text-lg leading-tight">{successCase.title}</p>
                              <p className="text-gray-300 text-sm mt-2 leading-relaxed">{successCase.subtitle}</p>
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {successCases.length > 1 && (
                      <>
                        <CarouselPrevious className="left-3 top-24 h-10 w-10 border-white/20 bg-black/45 text-white hover:bg-black/65 disabled:opacity-30" />
                        <CarouselNext className="right-3 top-24 h-10 w-10 border-white/20 bg-black/45 text-white hover:bg-black/65 disabled:opacity-30" />
                      </>
                    )}
                  </Carousel>
                </div>
              )}

              {/* CTA Button */}
              <button
                id="whatsapp-cta-btn"
                onClick={openWhatsApp}
                className="w-full font-bold py-5 rounded-2xl text-lg flex items-center justify-center gap-1 md:gap-3 transition-all duration-300 mb-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-xl shadow-green-600/30 hover:scale-[1.02] hover:shadow-green-600/50 active:scale-[0.98]"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Tenho interesse, quero saber mais!
              </button>


              <p className="text-center text-gray-300 text-xs font-medium">
                🔒 Ao agendar sua visita técnica gratuita, esta oferta fica reservada para você
                <> por <strong className="text-orange-300">30 dias</strong> </>
              </p>
              <p className="text-center text-gray-600 text-xs mt-1">
                Você será redirecionado para o WhatsApp
              </p>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="px-4 py-5 text-center text-xs text-gray-600">
          <p>© 2025 3M Solar · Todos os direitos reservados</p>
          <p className="mt-1">Preços e condições sujeitos à aprovação e vistoria técnica</p>
        </footer>
      </div>

      {/* Fade-in animation style */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default PartnerLanding;
